import React from 'react';

import { css } from '@emotion/react';
import { TextButton, Type } from '@robinhood/design';
import { NeutralColorToken } from '@robinhood/design';
import { PhoneNumberInputFormatter } from '@x1/util-format';
import { graphql } from 'relay-runtime';

import HelpModal from './HelpModal';
import { PhoneNumberPageMutation as PhoneNumberPageMutationType } from 'src/clients/Network/gql/build/PhoneNumberPageMutation.graphql';
import Button from 'src/components/creditApplication/Button';
import CreditApplicationPageLayout from 'src/components/creditApplication/CreditApplicationPageLayout';
import TextInput from 'src/components/creditApplication/TextInput';
import { useGlobalStore } from 'src/state/global';
import { useAsyncMutation } from 'src/utils/graphql';

const PhoneNumberPageMutation = graphql`
  mutation PhoneNumberPageMutation($creditApplicationId: String!, $e164: String!) {
    fraudAlertChallenge(q: { creditApplicationId: $creditApplicationId, e164: $e164 }) {
      success
    }
  }
`;

interface PropsIface {
  last4?: string;
  onNext: (e164: string) => void;
}

const PhoneNumberPage = ({ last4, onNext }: PropsIface) => {
  const creditApplicationId = useGlobalStore((s) => s.creditApplicationId);
  const [phoneNumber, setPhoneNumber] = React.useState<string>('');
  const [showModal, setShowModal] = React.useState<boolean>(false);
  const [commit, loading] = useAsyncMutation<PhoneNumberPageMutationType>(PhoneNumberPageMutation);

  const buttonState = React.useMemo(() => {
    if (loading) {
      return 'loading';
    }
    return PhoneNumberInputFormatter.deserialize(phoneNumber).length === 10 &&
      (last4 == null || phoneNumber.endsWith(last4))
      ? 'default'
      : 'disabled';
  }, [phoneNumber, last4, loading]);

  const onChangePhone = React.useCallback(({ value }: { value: string }) => {
    const formatted = PhoneNumberInputFormatter.serialize(value);
    setPhoneNumber(formatted);
  }, []);

  const onShowModal = React.useCallback(() => {
    setShowModal(true);
  }, []);

  const onDismissModal = React.useCallback(() => {
    setShowModal(false);
  }, []);

  const onSubmit = React.useCallback(async () => {
    try {
      if (!!creditApplicationId) {
        const e164 = `+1${PhoneNumberInputFormatter.deserialize(phoneNumber)}`;
        const res = await commit({
          variables: {
            creditApplicationId,
            e164,
          },
        });
        if (res.fraudAlertChallenge.success) {
          onNext(e164);
        } else {
          onShowModal();
        }
      }
    } catch (e) {
      onShowModal();
    }
  }, [onShowModal, onNext, commit, phoneNumber, creditApplicationId]);

  return (
    <CreditApplicationPageLayout
      showLogo={false}
      title='You have a fraud&nbsp;alert on your credit&nbsp;report'
      subtitle='You requested that credit bureaus such as Experian verify your identity before opening an account in your name.'
    >
      <div css={root}>
        <TextInput
          placeholder={last4 != null ? `Your phone ending in ${last4}` : 'Your phone number'}
          value={phoneNumber}
          onChange={onChangePhone}
          type='tel'
          inputMode='tel'
        />
        <div css={footer}>
          <Type.Text size='S'>
            Don’t know the number on your fraud alert?{' '}
            <TextButton
              label='Help'
              variant='inline'
              type='button'
              color={NeutralColorToken.Fg1}
              onClick={onShowModal}
            />
          </Type.Text>
          <Button label='Verify' state={buttonState} width='full' type='submit' onClick={onSubmit} />
        </div>
      </div>
      {showModal && <HelpModal onDismiss={onDismissModal} />}
    </CreditApplicationPageLayout>
  );
};

const root = css`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
`;

const footer = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  text-align: center;
`;

export default PhoneNumberPage;
